import { Controller } from "@hotwired/stimulus"
import MakaseController from "./makase_controller"

// usage
/*
  <!-- 기본 사용법 (slot 자동 생성) -->
  <div data-controller="makase-template">
    <button data-makase-template-target="clicktrigger">템플릿 열기</button>
    <template data-makase-template-target="template">
      <div>템플릿 내용</div>
    </template>
  </div>

  <!-- slot을 직접 지정 -->
  <div data-controller="makase-template"
       data-makase-template-render-value="close-then-open">
    <button data-makase-template-target="clicktrigger">클릭</button>
    <div data-makase-template-target="hovertrigger">호버</div>
    <template data-makase-template-target="template">
      <div>내용</div>
    </template>
    <div data-makase-template-target="slot">
      <!-- 여기에 템플릿이 렌더링됨 -->
    </div>
  </div>

  <!-- render 옵션 -->
  close-then-open (기본값): 초기에 닫혀있다가, 트리거 이벤트가 일어나면 열림
  open-then-close: 초기에 열려있다가, 트리거 이벤트가 일어나면 닫힘
  close-then-toggle: 초기에 닫혀있다가, 트리거 이벤트가 일어나면 토글
  open-then-toggle: 초기에 열려있다가, 트리거 이벤트가 일어나면 토글

  <!-- 이벤트를 통한 제어 (makase 컨트롤러와 함께 사용) -->
  $handle('#element', '->makase-template#open')
  $handle('#element', '->makase-template#close')
  $handle('#element', '->makase-template#toggle')
*/

export default class MakaseTemplateController extends Controller {
  // this.clicktriggerTarget
  // this.clicktriggerTargets
  // this.hovertriggerTarget
  // this.hovertriggerTargets
  // this.templateTarget - 템플릿 소스
  // this.slotTarget - 렌더링 슬롯 (옵션)
  static targets = ["clicktrigger", "hovertrigger", "template", "slot"]

  // this.renderValue : close-then-open|open-then-close|close-then-toggle|open-then-toggle
  static values = {
    render: { type: String, default: "close-then-open" },
  }

  connect() {
    this.isOpen = false
    this.insertedElement = null
    this.slotContainer = null

    // template target 검증
    if (!this.hasTemplateTarget) {
      console.warn(
        "makase-template: template target not found. Please add data-makase-template-target=\"template\" to a <template> element.",
        this.element
      )
    } else if (this.templateTarget.tagName !== "TEMPLATE") {
      console.warn(
        "makase-template: template target should be a <template> element, but got <" +
        this.templateTarget.tagName.toLowerCase() +
        ">",
        this.templateTarget
      )
    }

    // slot target 설정 (없으면 자동 생성)
    this.setupSlotContainer()

    // 이벤트 리스너 바인딩
    this.boundHandleClick = this.handleClick.bind(this)
    this.boundHandleHover = this.handleHover.bind(this)

    // renderer 이벤트 설정
    this.setupRendererEvents()

    // MakaseController.useEventAction을 사용하여 이벤트 핸들러 등록
    // ->makase-template#open, ->makase-template#close, ->makase-template#toggle
    MakaseController.useEventAction(this, "open")
    MakaseController.useEventAction(this, "close")
    MakaseController.useEventAction(this, "toggle")

    // recall event
    this.element.addEventListener("makase-template:recall", (event) => {
      let targetMatch = false
      if (typeof event.detail.target === "function") {
        targetMatch = event.detail.target(this.element)
      } else {
        targetMatch = (event.detail.target === this.element) || (this.element.id === event.detail?.id)
      }
      if (targetMatch) {
        event.stopPropagation()
        if (typeof event.detail.on === "function") return event.detail.on(this)
        console.warn("makase-template:recall event requires a controller or turboFrame function in its detail.")
      }
    })

    // 초기 상태 설정
    this.initializeTemplateState()
  }

  setupSlotContainer() {
    if (this.hasSlotTarget) {
      // slot target이 이미 있으면 그것을 사용
      this.slotContainer = this.slotTarget
    } else if (this.hasTemplateTarget) {
      // slot target이 없으면 template 다음에 생성
      this.slotContainer = document.createElement("div")
      this.slotContainer.setAttribute("data-makase-template-target", "slot")
      this.slotContainer.style.display = "none"

      // template 다음에 삽입
      if (this.templateTarget.nextSibling) {
        this.templateTarget.parentNode.insertBefore(this.slotContainer, this.templateTarget.nextSibling)
      } else {
        this.templateTarget.parentNode.appendChild(this.slotContainer)
      }
    }
  } disconnect() {
    this.removeRendererEvents()
    // useEventAction이 등록한 이벤트는 자동으로 정리됨

    // slot container 정리
    if (this.slotContainer) {
      this.slotContainer.innerHTML = ""
    }
  }

  setupRendererEvents() {
    // clicktrigger 이벤트 설정
    if (this.hasClicktriggerTarget) {
      this.clicktriggerTargets.forEach((renderer) => {
        renderer.addEventListener("click", this.boundHandleClick)
      })
    }

    // hovertrigger 이벤트 설정
    if (this.hasHovertriggerTarget) {
      this.hovertriggerTargets.forEach((renderer) => {
        renderer.addEventListener("mouseenter", this.boundHandleHover)
      })
    }
  }

  removeRendererEvents() {
    // clicktrigger 이벤트 제거
    if (this.hasClicktriggerTarget) {
      this.clicktriggerTargets.forEach((renderer) => {
        renderer.removeEventListener("click", this.boundHandleClick)
      })
    }

    // hovertrigger 이벤트 제거
    if (this.hasHovertriggerTarget) {
      this.hovertriggerTargets.forEach((renderer) => {
        renderer.removeEventListener("mouseenter", this.boundHandleHover)
      })
    }
  }

  initializeTemplateState() {
    // renderValue의 초기 상태에 따라 설정
    const [initialState] = this.renderValue.split("-then-")

    if (initialState === "open") {
      this.renderTemplate()
    } else {
      this.hideTemplate()
    }
  }

  handleClick(event) {
    event?.preventDefault()
    event?.stopPropagation?.()
    this.handleTrigger()
  }

  handleHover(event) {
    event?.preventDefault()
    event?.stopPropagation?.()
    this.handleTrigger()
  }

  handleTrigger() {
    // renderValue의 트리거 동작에 따라 처리
    const [, triggerAction] = this.renderValue.split("-then-")

    switch (triggerAction) {
      case "open":
        this.open()
        break
      case "close":
        this.close()
        break
      case "toggle":
        this.toggle()
        break
    }
  }

  open(event) {
    event?.stopPropagation?.()
    if (this.isOpen) return
    this.renderTemplate()
  }

  close(event) {
    event?.stopPropagation?.()
    if (!this.isOpen) return
    this.hideTemplate()
  }

  toggle(event) {
    if (this.isOpen) {
      this.close(event)
    } else {
      this.open(event)
    }
  }

  renderTemplate() {
    if (!this.hasTemplateTarget) return
    if (!this.slotContainer) return
    if (this.isOpen) return

    // template 내용을 가져와서 실제 DOM으로 변환
    const templateContent = this.templateTarget.content.cloneNode(true)

    // slot container에 템플릿 삽입
    this.slotContainer.innerHTML = ""
    this.slotContainer.appendChild(templateContent)
    this.slotContainer.style.display = ""

    this.isOpen = true

    // 열림 이벤트 발생
    this.element.dispatchEvent(new CustomEvent("makase-template:opened", {
      bubbles: true,
      detail: { slotContainer: this.slotContainer }
    }))
  }

  hideTemplate() {
    if (!this.isOpen) return

    // slot container 비우고 숨기기
    if (this.slotContainer) {
      this.slotContainer.innerHTML = ""
      this.slotContainer.style.display = "none"
    }

    this.isOpen = false

    // 닫힘 이벤트 발생
    this.element.dispatchEvent(new CustomEvent("makase-template:closed", {
      bubbles: true
    }))
  }

  // 외부에서 호출 가능한 메서드
  forceOpen() {
    this.open()
  }

  forceClose() {
    this.close()
  }

  getSlotContainer() {
    return this.slotContainer
  }

  // debug용 콘솔 메서드
  console(event) {
    event?.stopPropagation?.()
    console.log("turbine#console", { controller: this, event })
  }
}
